import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import { useMetaSelector } from '../../meta/reducer';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import type { WorkspaceGeneralInfo } from '@birdi/js-sdk/src/public/organisation';
import type { WidgetId } from '../tabs';

interface NumberWidget {
  id: WidgetId;
  label: string;
  getValue: (data: WorkspaceGeneralInfo) => number | string;
}

// Fixed order matching usage tab
const NUMBER_WIDGETS: NumberWidget[] = [
  {
    id: 'team-members',
    label: 'Team members',
    getValue: (data) => data.teamMembers,
  },
  {
    id: 'guest-collaborators',
    label: 'Guest collaborators',
    getValue: (data) => data.guestCollaborators,
  },
  {
    id: 'active-workspaces',
    label: 'Active workspaces',
    getValue: (data) => data.activeWorkspaces,
  },
  {
    id: 'maps-created',
    label: 'Maps created',
    getValue: (data) => data.mapsCreated,
  },
  {
    id: 'files-uploaded',
    label: 'Files uploaded',
    getValue: (data) => data.filesUploaded,
  },
  {
    id: 'storage-used',
    label: 'Storage used',
    getValue: (data) => `${parseFloat(data.storageUsed.toFixed(2))} GB`,
  },
];

export interface NumberWidgetsProps {
  selectedWidgetIds: WidgetId[];
  /** When provided, use this data and do not fetch. null = parent is loading. */
  workspaceGeneralInfo?: WorkspaceGeneralInfo | null;
  /** When workspaceGeneralInfo is provided, use this for loading state. */
  workspaceGeneralInfoLoading?: boolean;
}

export const NumberWidgets: React.FC<NumberWidgetsProps> = ({
  selectedWidgetIds,
  workspaceGeneralInfo: workspaceGeneralInfoProp,
  workspaceGeneralInfoLoading: workspaceGeneralInfoLoadingProp,
}) => {
  const meta = useMetaSelector();
  const theme = useTheme() as BirdiTheme;
  const [data, setData] = useState<WorkspaceGeneralInfo | null>(null);
  const [isLoading, setIsLoading] = useState(workspaceGeneralInfoProp === undefined);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  useEffect(() => {
    if (workspaceGeneralInfoProp !== undefined) {
      return;
    }
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await organisationApi.getWorkspaceGeneralInfo(stroot('weeh5y'));

        if (res.body) {
          setData(res.body);
        }
      } catch (error) {
        console.error('Error fetching workspace general info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [workspaceId, workspaceGeneralInfoProp]);

  const dataSource = workspaceGeneralInfoProp !== undefined ? workspaceGeneralInfoProp : data;
  const loading =
    workspaceGeneralInfoProp !== undefined
      ? Boolean(workspaceGeneralInfoLoadingProp)
      : isLoading;

  // Filter to only show selected number widgets, maintaining the fixed order
  const visibleWidgets = NUMBER_WIDGETS.filter((widget) =>
    selectedWidgetIds.includes(widget.id)
  );

  if (visibleWidgets.length === 0) {
    return null;
  }

  return (
    <div
      css={css`
        display: grid;
        padding: 0;
        /*
          auto-fit, not a fixed count — see the note in graph-widgets.tsx. The
          docked assistant panel removes 360px that a viewport media query can't
          see, so a hard "6 across" put six stat tiles into the space of three.
          Rows come out of the flow now rather than being pinned per breakpoint.
        */
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 0.5em;
        margin-bottom: 0.5em;
        min-width: 0;
      `}
    >
      {visibleWidgets.map((widget) => {
        const value = dataSource ? widget.getValue(dataSource) : (loading ? '...' : 0);
        const displayValue = typeof value === 'number' && value >= 1000
          ? value.toLocaleString()
          : value;

        return (
          <div
            key={widget.id}
            css={css`
              background: ${theme.mono0};
              border-radius: 1rem;
              padding: 1em 0.25em;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              min-width: 0;
              &:hover {
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 0.25rem;
                color: ${theme.midnight};
                text-align: center;
              `}
            >
              <div
                css={css`
                  font-size: 1.15rem;
                  font-weight: 550;
                `}
              >
                {displayValue}
              </div>
              <div
                css={css`
                  font-size: 0.9rem;
                `}
              >
                {widget.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

